import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileViewer } from '@capacitor/file-viewer';
import { Share } from '@capacitor/share';
import { Mediastore } from '@cotecna/capacitor-mediastore';

/**
 * Los archivos que la descarga offline guarda en `Directory.Data` (la carpeta privada de la app), con
 * Capacitor de punta a punta: se baja, se lee y se borra por el mismo camino.
 */
@Injectable({ providedIn: 'root' })
export class OfflineFileStore {
  /** Baja `url` a `path` (relativo a Data, crea las carpetas) y devuelve la ruta nativa del archivo. */
  async download(url: string, path: string): Promise<string> {
    // En Android `downloadFile` ignora `recursive` y falla si la carpeta no existe: se crea antes.
    const folder = path.substring(0, path.lastIndexOf('/'));
    if (folder) {
      await Filesystem.mkdir({ path: folder, directory: Directory.Data, recursive: true }).catch(() => {});
    }
    const response = await Filesystem.downloadFile({ url, path, directory: Directory.Data, recursive: true });
    if (!response.path) throw new Error('No se pudo guardar el archivo ' + path);
    return response.path;
  }

  /** Guarda un archivo que el alumno eligió (un `File`) en `path`; devuelve la ruta nativa. */
  async save(path: string, file: Blob): Promise<string> {
    const data = await blobToBase64(file);
    const response = await Filesystem.writeFile({ path, data, directory: Directory.Data, recursive: true });
    return response.uri;
  }

  /**
   * Lee un archivo guardado de vuelta como `Blob`, para armar un envío. No se arma un `File`: en el
   * dispositivo cordova-plugin-file reemplaza `window.File` por una clase que no es un `Blob`, y
   * `FormData` la mandaría como el texto «[object Object]». El nombre va en `append(key, blob, name)`.
   */
  async read(path: string, mime: string): Promise<Blob> {
    const result = await Filesystem.readFile({ path, directory: Directory.Data });
    return typeof result.data === 'string'
      ? await (await fetch(`data:${mime || 'application/octet-stream'};base64,${result.data}`)).blob()
      : result.data;
  }

  /** Borra un archivo. Si no existe, no es un error. */
  async remove(path: string): Promise<void> {
    await Filesystem.deleteFile({ path, directory: Directory.Data }).catch(() => {});
  }

  /** Borra una carpeta con todo lo que tiene. Si no existe, no es un error. */
  async removeDirectory(path: string): Promise<void> {
    await Filesystem.rmdir({ path, directory: Directory.Data, recursive: true }).catch(() => {});
  }

  /** La URL con la que el WebView puede abrir una ruta nativa (`<audio src>`, `<img src>`). */
  toWebUrl(nativePath: string): string {
    return Capacitor.convertFileSrc(nativePath);
  }

  /**
   * Le entrega al alumno un archivo que ya está en el dispositivo, sin red: lo mismo que haría la
   * descarga con conexión. En Android 10+ se copia a Descargas (`'downloads'`); en Android anterior e
   * iOS se abre la hoja de compartir del sistema con el archivo (`'shared'`).
   *
   * `downloadFile` devuelve en Android la ruta absoluta sin `file://`, y `Share` rechaza todo lo que no
   * empiece con `file:`: por eso se arma la URI aquí y nunca se le pasa la ruta tal cual.
   */
  async exportToDevice(nativePath: string, fileName: string): Promise<'downloads' | 'shared'> {
    if (Capacitor.getPlatform() === 'android') {
      const { androidSDKVersion } = await Device.getInfo();
      if ((androidSDKVersion ?? 0) >= 29) {
        // La ruta absoluta, no la URI: el plugin le corta `file:///` y la dejaría relativa.
        await Mediastore.saveToDownloads({ filename: fileName, path: nativePath.replace(/^file:\/\//, '') });
        return 'downloads';
      }
    }
    await Share.share({ url: toFileUri(nativePath) });
    return 'shared';
  }

  /**
   * Le muestra al alumno un archivo que ya está en el dispositivo, en un **visor** y no en una hoja de
   * compartir (que ofrecería WhatsApp, Slack…, cuando lo que quiere es verlo): en iOS la vista previa
   * nativa dentro de la app (PDF, imagen, video y Office), en Android la app que sabe abrir ese tipo.
   *
   * Si no hay visor (un .pptx sin suite Office, o un binario sin el plugin) se cae a la hoja de compartir,
   * para que el toque nunca quede sin respuesta. Cerrarla sin elegir nada no es un error.
   */
  async openWithSystem(nativePath: string, title: string): Promise<void> {
    try {
      await FileViewer.openDocumentFromLocalPath({ path: toAbsolutePath(nativePath) });
      return;
    } catch (error) {
      console.warn('[OfflineFileStore] sin visor para el archivo, se ofrece compartirlo', nativePath, error);
    }
    try {
      await Share.share({ url: toFileUri(nativePath), title, dialogTitle: 'Abrir con' });
    } catch (error) {
      if (isShareCanceled(error)) return;
      throw error;
    }
  }
}

/** La ruta absoluta sin `file://` ni `%20`, que es lo que pide el visor (`downloadFile` en iOS la trae con ambos). */
function toAbsolutePath(nativePath: string): string {
  if (!nativePath.startsWith('file://')) return nativePath;
  const path = nativePath.replace(/^file:\/\//, '');
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
}

/** `Share` rechaza con «Share canceled» cuando el alumno cierra la hoja sin elegir una app. */
function isShareCanceled(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String((error as { message?: string })?.message ?? error);
  return /cancel/i.test(message);
}

/** `file://` + la ruta, si todavía no lo trae. */
function toFileUri(nativePath: string): string {
  return nativePath.startsWith('file:') ? nativePath : `file://${nativePath}`;
}

/**
 * El contenido en base64 sin el prefijo `data:…;base64,` que deja `FileReader`.
 *
 * En el dispositivo cordova-plugin-file reemplaza `window.FileReader`, y su envoltura choca con el
 * parche de Zone.js: `onload` / `onerror` no llegan nunca y la promesa queda colgada. Se usa la
 * instancia original que Zone guarda, como en `video-upload.service.ts`.
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const wrapped = new FileReader();
    const reader: FileReader = (wrapped as any)['__zone_symbol__originalInstance'] || wrapped;
    reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el archivo'));
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.readAsDataURL(blob);
  });
}
